import { Tutee } from '../types/tuition';
import { fetchTutees, fetchTuteeById } from '../services/tuteeService';

/**
 * Tutee Configuration
 * Empty fallback — do not hardcode tutee data or PINs here.
 * If Supabase is unavailable, the app will show no tutees.
 */
const FALLBACK_TUTEES: Tutee[] = [];

// Cache for tutees
let tuteesCache: Tutee[] | null = null;

/**
 * Fetch tutees from Supabase with fallback
 */
export const getTutees = async (): Promise<Tutee[]> => {
  try {
    const tutees = await fetchTutees();
    tuteesCache = tutees;
    return tutees;
  } catch (error) {
    console.warn('Failed to fetch tutees from Supabase, using fallback:', error);
    return FALLBACK_TUTEES;
  }
};

/**
 * Get tutee by ID (from cache or Supabase)
 */
export const getTuteeById = async (id: string): Promise<Tutee | null> => {
  try {
    // Try Supabase first
    const tutee = await fetchTuteeById(id);
    if (tutee) {
      // Update cache
      if (tuteesCache) {
        const index = tuteesCache.findIndex(t => t.id === id);
        if (index >= 0) {
          tuteesCache[index] = tutee;
        } else {
          tuteesCache.push(tutee);
        }
      }
      return tutee;
    }
  } catch (error) {
    console.warn('Failed to fetch tutee from Supabase:', error);
  }
  
  // Fallback to local
  return FALLBACK_TUTEES.find(tutee => tutee.id === id) || null;
};

/**
 * Get tutee by ID (synchronous, from cache or fallback)
 */
export const getTuteeByIdSync = (id: string): Tutee | undefined => {
  if (tuteesCache) {
    return tuteesCache.find(tutee => tutee.id === id);
  }
  return FALLBACK_TUTEES.find(tutee => tutee.id === id);
};

/**
 * Verify PIN for a tutee
 */
export const verifyPin = async (tuteeId: string, pin: string): Promise<boolean> => {
  const tutee = await getTuteeById(tuteeId);
  return tutee?.pin === pin;
};

/**
 * Verify PIN synchronously (from cache or fallback)
 */
export const verifyPinSync = (tuteeId: string, pin: string): boolean => {
  const tutee = getTuteeByIdSync(tuteeId);
  return tutee?.pin === pin;
};

